import { memo, useMemo, useState } from 'react';
import {
  Box,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Typography
} from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import FiberManualRecordOutlinedIcon from '@mui/icons-material/FiberManualRecordOutlined';

import type { OpenerGroup } from '../../api/useGroups';
import {
  OpenerGroupPodState,
  useOpenerGroupsPodState
} from '../../api/useOpenerGroupPods';
import { useTriageIndex } from '../../api/useTriageIndex';
import { TriageIndexNotice } from '../triage/TriageIndexNotice';
import { TriageMarks } from '../triage/TriageMarks';
import { triageGroupDeepLink } from '../triage/triageDeepLink';
import { hasTriageMarks } from '../triage/triageMarkText';
import {
  TRIAGE_UNGROUPED_KEY,
  TRIAGE_UNGROUPED_LABEL,
  resolveGroupTriage
} from './triageRowMarks';
import {
  GROUP_POD_STATUS_DOT_COLORS,
  GROUP_POD_STATUS_LABELS,
  GroupPodStatus,
  deriveGroupPodStatus
} from './groupPodStatus';

/**
 * The opener's group selector. Carries the SAME two facts the group-picker
 * landing shows per group — the pod-status dot and the pair count — because the
 * operator picks a group once and then lives inside it, and used to have to go
 * back to `'all'` just to see whether the group's engine was up.
 *
 * It owns the pod-state poll itself so the dots' data lives with the dots — and
 * so the page does not gain a THIRD observer of the key. (It does not remove a
 * page-scope subscription: `useEntrySocket` already observes the same key from
 * EntryPage while a concrete group is selected, so EntryPage re-renders on that
 * tick either way.)
 *
 * The poll runs ONLY while the menu is open, because that is the only time the
 * ~30 option dots exist (the Menu is not `keepMounted`). The one always-visible
 * dot is the closed box's, and it renders only for a concrete group — exactly
 * when `GroupPodControls` is mounted beside it keeping the same cache warm. A
 * disabled react-query observer still reads that cache and re-renders on its
 * updates, so the closed dot stays live for free. Without this gate the
 * component would add a third 5s interval on `GET /api/system/opener/groups/state`
 * (v3 gives every observer its OWN timer — sharing a key does not share it) and
 * a brand-new one in the `'ungrouped'` view, for dots nobody is looking at.
 */

/** Selected-value sentinel for "All groups" — mirrors EntryPage's constant. */
export const ALL_GROUPS_VALUE = 'all';

/**
 * `undefined` pod state means the poll has not answered YET (cold load) or is
 * failing — NOT that the group is stopped. `deriveGroupPodStatus` collapses
 * both into `'off'`, which on a bare dot would assert "every group is stopped"
 * while they are all trading. So the unknown case gets its own hollow dot: the
 * landing and the bucket rail can afford to lean on `deriveGroupPodStatus`
 * because they print the status word / tooltip beside the dot; here the dot is
 * alone.
 */
type DotStatus = GroupPodStatus | 'unknown';

const StatusDot = ({ status }: { status: DotStatus }) =>
  status === 'unknown' ? (
    <FiberManualRecordOutlinedIcon
      titleAccess="Pods: unknown"
      // TWO channels away from `off` (a filled `grey.700` disc): a RING, and a
      // brighter grey. One greyscale step apart at 11px on a dark theme is not
      // a difference an operator reads at a glance, and "the poll is failing"
      // vs "this group is stopped" is exactly the confusion this dot exists to
      // remove.
      sx={{ fontSize: '0.7rem', flexShrink: 0, color: 'grey.400' }}
    />
  ) : (
    <FiberManualRecordIcon
      // The status is otherwise conveyed by COLOR ALONE, and the confusable
      // pair is running (green) vs panicked (red) — the two states with the
      // widest operational gap. `titleAccess` renders a real <title>, so it is
      // both the screen-reader text and a native hover tooltip.
      titleAccess={`Pods: ${GROUP_POD_STATUS_LABELS[status]}`}
      sx={{
        fontSize: '0.7rem',
        flexShrink: 0,
        color: GROUP_POD_STATUS_DOT_COLORS[status]
      }}
    />
  );

export interface GroupSelectProps {
  /** `'all'` sentinel, the ungrouped sentinel, or a group id. */
  value: string;
  onChange: (value: string) => void;
  /** Already sorted by the caller with the shared `sortGroups`. */
  groups: OpenerGroup[];
  /** Sentinel string for the no-group bucket (owned by `groupFilter`). */
  ungroupedValue: string;
  /**
   * Group id selected but absent from `groups` (still loading, or deleted
   * elsewhere) — MUI warns on an out-of-range value, so it gets a hidden
   * option. `null` when the selection resolves normally.
   */
  unresolvedGroupId: string | null;
}

export const GroupSelect = memo(
  ({
    value,
    onChange,
    groups,
    ungroupedValue,
    unresolvedGroupId
  }: GroupSelectProps) => {
    const [open, setOpen] = useState(false);
    const { data: podStates } = useOpenerGroupsPodState({ enabled: open });
    // Gated on `open` for the same reason the pod poll is: the badges only
    // exist while the menu is (the Menu is not `keepMounted`), and an ungated
    // observer would add a THIRD interval to this toolbar. The opener table
    // beside it already holds a live index, so the cache is warm on open.
    const {
      data: triageIndex,
      isStale: triageStale,
      lastSuccessAt: triageLastSuccessAt
    } = useTriageIndex({ enabled: open });
    const ungroupedTriage = resolveGroupTriage(triageIndex, TRIAGE_UNGROUPED_KEY);
    const podStateByGroup = useMemo(() => {
      const map = new Map<string, OpenerGroupPodState>();
      for (const s of podStates ?? []) map.set(s.groupId, s);
      return map;
    }, [podStates]);

    // "Unknown" is NEVER-ANSWERED, not "the last fetch errored": react-query
    // keeps `data` across a failed background refetch, and `GroupPodControls`
    // renders the retained truth as a green Running chip right beside this
    // selector. Gating on the query's `isSuccess` instead would flip every dot
    // hollow for the ~7s of a pod rollout's retries while the chip next to it
    // says Running — two contradictory answers in one toolbar.
    const statusOf = (groupId: string): DotStatus =>
      podStates === undefined
        ? 'unknown'
        : deriveGroupPodStatus(podStateByGroup.get(groupId));

    return (
      <Select
        size="small"
        value={value}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        onChange={(e) => onChange(e.target.value)}
        sx={{
          width: 170,
          height: 28,
          fontSize: '0.75rem',
          '& .MuiSelect-select': { py: 0.5, px: 1 }
        }}
        inputProps={{ 'aria-label': 'Filter pairs by group' }}
        // The closed box keeps the status dot (is this group running?) but
        // drops the pair count — 170px only fits the name. The count lives in
        // the open list, where the landing's two facts are both visible.
        renderValue={selected => {
          if (selected === ALL_GROUPS_VALUE) return 'All groups';
          if (selected === ungroupedValue) return 'Ungrouped';
          const g = groups.find(x => x._id === selected);
          return (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ minWidth: 0 }}
            >
              <StatusDot status={statusOf(selected)} />
              <Typography noWrap sx={{ fontSize: '0.75rem' }}>
                {g?.name ?? '…'}
              </Typography>
            </Stack>
          );
        }}
      >
        {/* 🚨 The `⚠?` on the toolbar is BEHIND this menu's backdrop, so
            without a copy in here a list with no badges over an index that
            could not be read presents as "no group needs triage" (§6.7). It
            renders nothing while the index is readable, fresh and complete.

            `ListSubheader` for two reasons, both structural: it is STICKY, so
            the menu opening scrolled to the selected group (item #22 of a
            30-group slate) cannot leave the notice 20 rows above the fold —
            which would restore the very §6.7 lie; and MUI skips it when
            picking the list's active item (`muiSkipListHighlight`), so it can
            never be handed the `tabindex` that makes `Select`'s click handler
            resolve a click here into `onChange(undefined)`. */}
        <ListSubheader
          data-triage-notice-slot="group-select"
          sx={{
            px: 2,
            py: 0.5,
            lineHeight: 'normal',
            '&:empty': { display: 'none' }
          }}
        >
          <TriageIndexNotice
            index={triageIndex}
            isStale={triageStale}
            lastSuccessAt={triageLastSuccessAt}
          />
        </ListSubheader>
        <MenuItem value={ALL_GROUPS_VALUE} sx={{ fontSize: '0.75rem' }}>
          All groups
        </MenuItem>
        <MenuItem value={ungroupedValue} sx={{ fontSize: '0.75rem' }}>
          Ungrouped
        </MenuItem>
        {groups.map(g => (
          <MenuItem key={g._id} value={g._id} sx={{ fontSize: '0.75rem' }}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              sx={{ width: '100%', minWidth: 0 }}
            >
              <StatusDot status={statusOf(g._id)} />
              <Typography
                noWrap
                sx={{ fontSize: '0.75rem', flexGrow: 1, minWidth: 0 }}
              >
                {g.name}
              </Typography>
              <Typography
                sx={{ fontSize: '0.65rem', color: 'grey.500', flexShrink: 0 }}
              >
                {g.pairCount} pair{g.pairCount === 1 ? '' : 's'}
              </Typography>
              {/* The badge is a link out of this menu, not a selection. */}
              <Box
                component="span"
                data-triage-group={g._id}
                onClick={e => e.stopPropagation()}
              >
                <TriageMarks
                  counts={resolveGroupTriage(triageIndex, g._id)}
                  variant="picker"
                  deepLinkTo={triageGroupDeepLink(g._id)}
                />
              </Box>
            </Stack>
          </MenuItem>
        ))}
        {/* 🚨 The `null` bucket, at the bottom and on its own — never folded
            into a real group. Not a selectable option: there is no such group
            to open, only cards to triage. */}
        {hasTriageMarks(ungroupedTriage) && (
          <ListSubheader
            data-triage-ungrouped="1"
            // 🚨 Pinned to the BOTTOM — the same reason the notice is pinned to
            // the top, on the entry that can least afford it. This bucket is by
            // definition the cards nobody could place in a group; it is never
            // folded into a real group and never dropped, and at the end of a
            // 30-group slate it opens below the fold with the menu scrolled to
            // the selected row. `disableSticky` turns off MUI's TOP pinning so
            // this does not fight the notice for the same edge.
            disableSticky
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 2,
              py: 0.75,
              lineHeight: 'normal',
              position: 'sticky',
              bottom: 0,
              top: 'auto',
              zIndex: 1,
              bgcolor: 'background.paper',
              borderTop: '1px solid rgba(255,255,255,0.12)'
            }}
          >
            <Typography sx={{ fontSize: '0.7rem', color: 'grey.500' }}>
              {TRIAGE_UNGROUPED_LABEL}
            </Typography>
            <TriageMarks
              counts={ungroupedTriage}
              variant="picker"
              unattributedNote
              deepLinkTo={triageGroupDeepLink(TRIAGE_UNGROUPED_KEY)}
            />
          </ListSubheader>
        )}
        {unresolvedGroupId && (
          <MenuItem value={unresolvedGroupId} sx={{ display: 'none' }}>
            …
          </MenuItem>
        )}
      </Select>
    );
  }
);

GroupSelect.displayName = 'GroupSelect';
